import makeRequest from "./makeRequest";
import getRuntimeConstants from "../runtimeConstants";
import {
  YMAL_BTL_HEADER,
  MIGHT_LIKE_API_LIMIT,
  YMAL_ANALYTICS_TRACKING,
} from "../constants";
import { formatUrl, getProductUrl } from "../utils/url";
import { getFeature, onReady } from "../utils/features";
import { getAuthorisationHeader } from "../utils/identity";

const getUnavailable = (isDiscontinued, isUnavailableForUS) => {
  if (isDiscontinued) return "_discontinued";
  if (isUnavailableForUS) return "_us-unavailable";
  return "";
};

const getMightLikeUrl = ({
  brandName,
  name,
  id,
  index,
  type,
  scope,
  isMobile,
  isDiscontinued,
  isUnavailableForUS,
  pageArea,
}) => {
  const productUrl = getProductUrl(brandName, name, id);
  let ctaref, featureref1;

  if (type === "complementary") {
    ctaref = "complementary+items";
    featureref1 = "complementary+items";
  } else if (scope === YMAL_BTL_HEADER && YMAL_ANALYTICS_TRACKING[pageArea]) {
    const platform = isMobile ? "mobile" : "desktop";
    const tracking = YMAL_ANALYTICS_TRACKING[pageArea][platform];
    ctaref = tracking.ctaref;
    featureref1 = tracking.featureref1;
  } else {
    ctaref = "we+recommend+carousel";
    featureref1 = "we+recommend+pers";
  }

  const unavailable = getUnavailable(isDiscontinued, isUnavailableForUS);

  return `${productUrl}#ctaref-${ctaref}_${index}${unavailable}&featureref1-${featureref1}`;
};

const getHeaderScope = ({ type, scope, isInStock }) => {
  if (scope) {
    return scope;
  }

  if (type === "complementary") {
    return isInStock ? `pdp_comp` : `pdp_comp_oos`;
  }

  return isInStock ? `pdp_ymal` : `pdp_ymal_oos`;
};

const getRequestProperties = ({
  authorisationHeader,
  isInStock,
  isDiscontinued,
  isUnavailableForUS,
  type,
  scope,
  pageArea,
}) => {
  const { mightLikeApiPath, recsApiTimeout, isMobile } = getRuntimeConstants();

  return {
    url: mightLikeApiPath,
    timeout: recsApiTimeout,
    headers: {
      authorization: authorisationHeader,
      "asos-c-recs-scope": getHeaderScope({ type, scope, isInStock }),
    },
    requestType: "MIGHT_LIKE_API",
    apiName: "Presentation",
    caller: type === "complementary" ? "People Also Bought" : "YMAL",
    mapData: (data) =>
      mapRecsData({
        data,
        type,
        scope,
        isMobile,
        isDiscontinued,
        isUnavailableForUS,
        pageArea,
      }),
  };
};

const mapRecsData = ({
  data,
  type,
  scope,
  isMobile,
  isDiscontinued,
  isUnavailableForUS,
  pageArea,
}) => {
  return data.recommendations.map(
    (
      { id, imageUrl, name, brandName, productCode, colourWayId, price },
      index,
    ) => {
      return {
        productId: id,
        productCode,
        url: getMightLikeUrl({
          brandName,
          name,
          id,
          index,
          type,
          scope,
          isMobile,
          isDiscontinued,
          isUnavailableForUS,
          pageArea,
        }),
        imageUrl: formatUrl(imageUrl),
        productTitle: name,
        brandName,
        colourWayId,
        priceData: price,
      };
    },
  );
};

const getRecsMVT = async () => {
  await onReady();

  const recsAlgorithm = getFeature("pdp-ymal-alg");
  const recsVersion = recsAlgorithm.getVariables()?.tstRecVersion;

  if (recsAlgorithm.isEnabled() && recsVersion !== "") {
    return recsVersion;
  }
};

export default async function getRecommendations({
  isInStock,
  isDiscontinued,
  isUnavailableForUS,
  type,
  productId,
  handleErrorReturnValue = () => [],
  scope,
  pageArea,
}) {
  const {
    webContext: {
      browseCountry,
      browseCurrency,
      browseLanguage,
      keyStoreDataversion,
      storeId,
    },
    product: { id },
    features: { recommendationsEnabled },
  } = getRuntimeConstants();

  if (!recommendationsEnabled) {
    return [];
  }

  const authorisationHeader = await getAuthorisationHeader();

  const params = {
    productids: productId ? productId : id,
    type: type ? type : "ymal",
    expand: "products",
    limit: MIGHT_LIKE_API_LIMIT,
    store: storeId,
    country: browseCountry,
    currency: browseCurrency,
    lang: browseLanguage,
    keyStoreDataversion,
  };

  const recsMVT = await getRecsMVT();

  if (recsMVT) {
    params["tst-rec-version"] = recsMVT;
  }

  return makeRequest({
    params,
    ...getRequestProperties({
      authorisationHeader,
      isInStock,
      isDiscontinued,
      isUnavailableForUS,
      type,
      scope,
      pageArea,
    }),
    handleErrorReturnValue,
  });
}
